import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Portal,
  CloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Flex,
  IconButton,
  Image
} from '@chakra-ui/react';
import { LuArrowUp, LuArrowDown, LuGripVertical } from 'react-icons/lu';
import { COLORS } from '../../../theme';

export default function ReorderCategories({
  isOpen,
  onClose,
  items,
  isSaving,
  onSave
}) {
  const [localItems, setLocalItems] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLocalItems([...items]);
    }
  }, [isOpen, items]);

  const moveItem = (index, direction) => {
    const updated = [...localItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= updated.length) return;

    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setLocalItems(updated);
  };

  const handleSave = () => {
    onSave(localItems);
  };

  const borderColor = { _light: 'gray.200', _dark: 'gray.700' };
  const itemBg = { _light: 'white', _dark: 'gray.900' };
  const hoverBg = { _light: 'gray.50', _dark: 'gray.800' };

  return (
    <Dialog.Root
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="80vh">
            <Dialog.Header borderBottomWidth="1px" borderColor={borderColor}>
              <Dialog.Title>Reorder Categories</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={4}>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Use the arrows to change the display order of your gallery
                categories.
              </Text>
              <VStack gap={2} align="stretch">
                {localItems.map((item, index) => (
                  <Flex
                    key={item.urlPath}
                    p={3}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    align="center"
                    bg={itemBg}
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Box color="gray.400" mr={3}>
                      <LuGripVertical />
                    </Box>
                    <Image
                      src={item.images && item.images[0] ? item.images[0] : ''}
                      alt={item.title}
                      w="60px"
                      h="40px"
                      borderRadius="sm"
                      objectFit="cover"
                      mr={3}
                      fallbackSrc="https://via.placeholder.com/60x40?text=No+Img"
                    />
                    <Box flex={1} overflow="hidden">
                      <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                        {item.title}
                      </Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {item.category} • {item.images ? item.images.length : 0}{' '}
                        images
                      </Text>
                    </Box>
                    <HStack gap={1} ml={4}>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        aria-label="Move Up"
                      >
                        <LuArrowUp />
                      </IconButton>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === localItems.length - 1}
                        aria-label="Move Down"
                      >
                        <LuArrowDown />
                      </IconButton>
                    </HStack>
                  </Flex>
                ))}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" borderColor={borderColor}>
              <Flex gap={3} justify="flex-end" w="full">
                <Button onClick={onClose} variant="ghost">
                  Cancel
                </Button>
                <Button
                  colorPalette="orange"
                  bg={COLORS.brand.primary}
                  _hover={{ bg: COLORS.brand.hover }}
                  onClick={handleSave}
                  loading={isSaving}
                  px={8}
                >
                  Save New Order
                </Button>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
